import React, { useEffect, useMemo, useState } from "react";
import styles from "./languageSelectorModal.module.scss";
import Button from "@Components/Button";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useIsDesktop from "hooks/useIsDesktop";
import useTranslationHook from "hooks/useTranslationHook";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import wrapperHOC from "@Utils/wrapperHOC";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { languagesForAmberUserWebsite } from "locales/langConfig/langConfig";
import useLanguageSelector from "hooks/useLanguageSelector";
import currencyData, { ALLOWED_CURRENCY_CODES } from "constants/currencyConstants";
import setCookie from "helpers/setCookie";
import getCookie from "helpers/getCookie";
import config from "@Config/index";

type LanguageOption = {
  code: string;
  label: string;
  flag?: any;
  shortDisplayText?: string;
};

interface LanguageSelectorModalProps {
  onClose: () => void;
  isCurrencyFeatureEnabled: boolean;
}

const getAvailableLanguages = (userLanguage: string): LanguageOption[] => {
  const languages = languagesForAmberUserWebsite.map((lang) => ({
    code: lang.code,
    label: lang.nativeLabel,
    flag: lang.flag,
    shortDisplayText: lang.shortDisplayText,
  }));

  return languages.sort((a, b) => {
    if (a.code === userLanguage) return -1;
    if (b.code === userLanguage) return 1;
    return 0;
  });
};

const LanguageSelectorModal: React.FC<LanguageSelectorModalProps> = ({
  onClose,
  isCurrencyFeatureEnabled,
}) => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslationHook();

  const { userLanguage } = useSelector(({ appData }: AppState) => ({
    userLanguage: appData.userLanguage,
  }));

  const handleEventEmitter = ({ action, ...rest }: any) => {
    gtagEventEmitter({ action, category: "LANGUAGE_MODAL", ...rest });
  };
  const { handleLanguageChange } = useLanguageSelector({ handleEventEmitter });

  const defaultLanguage = useMemo(() => userLanguage || "en", [userLanguage]);

  const [activeTab, setActiveTab] = useState<"language" | "currency">("language");
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const [selectedCurrency, setSelectedCurrency] = useState("default");
  const [currencies, setCurrencies] = useState<typeof currencyData>([]);

  /** Filter currencies based on feature + allowed list */
  const filteredCurrencies = useMemo(() => {
    if (!isCurrencyFeatureEnabled) return [];
    return currencyData.filter((cur) => ALLOWED_CURRENCY_CODES.includes(cur.standard_name));
  }, [isCurrencyFeatureEnabled]);

  /** Sync language */
  useEffect(() => {
    setSelectedLanguage(defaultLanguage);
  }, [defaultLanguage]);

  /** Setup currency list + cookie ordering */
  useEffect(() => {
    if (!isCurrencyFeatureEnabled) return;

    const userCurrencyCookie = getCookie("user_currency");

    const orderedCurrencies = [...filteredCurrencies];

    if (userCurrencyCookie) {
      setSelectedCurrency(userCurrencyCookie);

      orderedCurrencies.sort((a, b) => {
        if (a.standard_name === userCurrencyCookie) return -1;
        if (b.standard_name === userCurrencyCookie) return 1;
        return 0;
      });
    }

    setCurrencies(orderedCurrencies);
  }, [filteredCurrencies, isCurrencyFeatureEnabled]);

  /** Safety: reset tab if feature disabled */
  useEffect(() => {
    if (!isCurrencyFeatureEnabled && activeTab === "currency") {
      setActiveTab("language");
    }
  }, [isCurrencyFeatureEnabled, activeTab]);

  const onApply = () => {
    // Close modal first to ensure clean state before navigation
    onClose();

    if (activeTab === "language") {
      handleEventEmitter({ action: "APPLY_LANGUAGE", language: selectedLanguage });

      if (config.IS_CHINA_APP && selectedLanguage !== "zh") {
        // Append param to current URL so buildLanguageUrl (preserveSearch) carries it to the target domain
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set("amber_user_lang", selectedLanguage);
        window.history.replaceState(null, "", currentUrl.toString());
      }

      // Use setTimeout to ensure modal closes before navigation
      setTimeout(() => {
        handleLanguageChange(selectedLanguage);
      }, 100);
    } else {
      handleEventEmitter({ action: "APPLY_CURRENCY", currency: selectedCurrency });
      const baseDomain = window.location.hostname.split(".").slice(-2).join(".");
      if (selectedCurrency === "default") {
        // delete cookie if exists
        setCookie({
          cname: "user_currency",
          cvalue: "",
          exdays: -1, // expire immediately
          secure: true,
          domain: `.${baseDomain}`,
        });
      } else {
        // set cookie
        setCookie({
          cname: "user_currency",
          cvalue: selectedCurrency,
          exdays: 365,
          secure: true,
          domain: `.${baseDomain}`,
        });
      }

      // Use setTimeout to ensure modal closes before reload
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  };

  const onReset = () => {
    setSelectedLanguage(defaultLanguage);
    if (isCurrencyFeatureEnabled) setSelectedCurrency("default");
    handleEventEmitter({ action: "reset_preferences" });
  };

  const availableLanguages = getAvailableLanguages(userLanguage || "en");
  const languages = availableLanguages;

  const handleActiveTabChange = (tab: "language" | "currency") => {
    setActiveTab(tab);
    handleEventEmitter({ action: `clicked_${tab}_tab` });
  };

  return (
    <div className={styles.container}>
      <div className={styles.modal_header}>
        <h4>{t("Select Preferences")}</h4>
      </div>

      <div className={formatClassNames(styles.body, isDesktop && styles.desktop)}>
        <div className={styles.sidebar}>
          <div
            className={formatClassNames(
              styles.sidebarItem,
              activeTab === "language" && styles.active,
            )}
            onClick={() => handleActiveTabChange("language")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleActiveTabChange("language");
            }}
            role="button"
            tabIndex={0}
          >
            {t("Language")}
            <span className={styles.checkIcon}>{activeTab === "language" ? "✓" : ""}</span>
          </div>

          {isCurrencyFeatureEnabled && (
            <div
              className={formatClassNames(
                styles.sidebarItem,
                activeTab === "currency" && styles.active,
              )}
              onClick={() => handleActiveTabChange("currency")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleActiveTabChange("currency");
              }}
              role="button"
              tabIndex={0}
            >
              {t("Currency")}
              <span className={styles.checkIcon}>{activeTab === "currency" ? "✓" : ""}</span>
            </div>
          )}
        </div>

        <div className={styles.content}>
          <ul className={styles.list}>
            {activeTab === "language" &&
              languages.map((lang) => (
                <li key={lang.code}>
                  <button
                    type="button"
                    className={formatClassNames(
                      styles.option,
                      selectedLanguage === lang.code && styles.optionActive,
                    )}
                    onClick={() => setSelectedLanguage(lang.code)}
                  >
                    <span className={styles.left}>
                      {lang.flag &&
                        typeof lang.flag === "function" &&
                        React.createElement(lang.flag)}
                      <span className={styles.optionLabel}>{lang.label}</span>
                    </span>
                    <span
                      className={formatClassNames(
                        styles.radio,
                        selectedLanguage === lang.code && styles.checked,
                      )}
                    />
                  </button>
                </li>
              ))}

            {isCurrencyFeatureEnabled &&
              activeTab === "currency" &&
              currencies.map((cur, index) => (
                <React.Fragment key={cur.standard_name}>
                  {index === 0 && (
                    <li>
                      <button
                        type="button"
                        className={formatClassNames(
                          styles.option,
                          selectedCurrency === "default" && styles.optionActive,
                        )}
                        onClick={() => setSelectedCurrency("default")}
                      >
                        <span className={formatClassNames(styles.left, styles.optionLabel)}>
                          Default
                        </span>
                        <span
                          className={formatClassNames(
                            styles.radio,
                            selectedCurrency === "default" && styles.checked,
                          )}
                        />
                      </button>
                    </li>
                  )}

                  <li>
                    <button
                      type="button"
                      className={formatClassNames(
                        styles.option,
                        selectedCurrency === cur.standard_name && styles.optionActive,
                      )}
                      onClick={() => setSelectedCurrency(cur.standard_name)}
                    >
                      <span className={formatClassNames(styles.left, styles.optionLabel)}>
                        {`${cur.standard_name} (${cur.symbol}) · ${cur?.full_name?.split("(")[0]}`}
                      </span>
                      <span
                        className={formatClassNames(
                          styles.radio,
                          selectedCurrency === cur.standard_name && styles.checked,
                        )}
                      />
                    </button>
                  </li>
                </React.Fragment>
              ))}
          </ul>
        </div>
      </div>

      <div
        className={formatClassNames(styles.modal_footer, isDesktop && styles.modal_footer_desktop)}
      >
        <Button
          className={styles.btn_width}
          variant={isDesktop ? "subtle" : "outline"}
          onClick={onReset}
          {...(isDesktop ? { size: "lg" } : {})}
          greyDisabled={
            selectedLanguage === defaultLanguage &&
            (!isCurrencyFeatureEnabled || selectedCurrency === "default")
          }
        >
          <span className={styles.resetText}>{t("Reset")}</span>
        </Button>
        <Button
          className={styles.btn_apply}
          variant="primary"
          onClick={onApply}
          {...(isDesktop ? { size: "lg" } : {})}
        >
          {t("Apply")}
        </Button>
      </div>
    </div>
  );
};

export default wrapperHOC(LanguageSelectorModal, {
  componentName: "LanguageSelectorModal",
  showForChina: true,
});
